class OnBoardDiagnostics {
  // On-Board Diagnostics (OBD) is responsible for inspecting, evaluating
  // and finally deciding if or when to trigger the AD spot fallback content.
  // Lifecycle of OBD
  // Inspect: The constructor triggers the inspect function.
  // Evaluate: At the end of inspect function the evaluate function is triggered.
  // Decide: The evaluate function makes a decision if ad spot fallback is required.
  #googleAdUrlAccessible;

  #localStorageAccessible;

  #inspectionCycle;

  #isGoogleTagLoaded;

  #isMarketingAllowed;

  #fallbackTriggered;

  constructor() {
    this.#fallbackTriggered = false;
    this.#isGoogleTagLoaded = false;
    this.#inspectionCycle = 0;
    // delay between the inspection cycles is set to 5 sec
    this.inspectionDelay = 5000;
  }

  async detectAdBlock() {
    // This implementation is based on this stackoverflow post
    // https://stackoverflow.com/questions/4869154/how-to-detect-adblock-on-my-website
    const googleAdUrl = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
    try {
      const request = fetch(new Request(googleAdUrl));
      request.catch(() => {
        this.#googleAdUrlAccessible = false;
      });
      request.then(() => {
        this.#googleAdUrlAccessible = true;
      });
      await request;
    } catch (e) {
      this.#googleAdUrlAccessible = false;
    }
  }

  inspectGoogleTag() {
    // inspects googtag object to ensure it has loaded
    try {
      /* eslint-disable no-underscore-dangle */
      this.#isGoogleTagLoaded = !!(window.googletag && window.googletag.apiReady === true);
    } catch (e) {
      // catches a ReferenceError when marketing is disabled in Osana
      this.#isGoogleTagLoaded = false;
    }
  }

  inspectOsanoPreference() {
    // inspects users marketing preference stated in Osano
    try {
      this.#isMarketingAllowed = window.Osano.cm.marketing;
    } catch (e) {
      this.#isMarketingAllowed = false;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  waitForOsanoBannerInteraction() {
    // Waits for user to interact with the osano cookie banner before we determine if we want
    // to show the fallback ads. Uses the osano-cm-consent-saved event which fires when a user
    // registers or has previously registered their preferences. Can see doc for more details:
    // eslint-disable-next-line max-len
    // https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api#-osano-cm-consent-saved
    return new Promise((resolve) => {
      window.Osano.cm.addEventListener('osano-cm-consent-saved', (event) => {
        resolve(event);
      });
    });
  }

  inspectLocalStorage() {
    // inspects local storage to ensure it is accessible
    // We implement a solution similar to the js library modernizr
    // https://github.com/Modernizr/Modernizr/blob/master/feature-detects/storage/localstorage.js
    // Note: Although this signal is inspected we are not evaluating this to make a decision.
    try {
      localStorage.setItem('localStorage', 'check');
      localStorage.removeItem('localStorage');
      this.#localStorageAccessible = true;
    } catch (e) {
      // local storage could not be available due to privacy settings or something
      this.#localStorageAccessible = false;
    }
  }

  getDiagnosticPrintOut() {
    // this function spits out the current state of private variables
    const diagnosticPrintOut = {
      googleAdUrlAccessible: this.#googleAdUrlAccessible,
      localStorageAccessible: this.#localStorageAccessible,
      isGoogleTagLoaded: this.#isGoogleTagLoaded,
      isMarketingAllowed: this.#isMarketingAllowed,
    };
    return diagnosticPrintOut;
  }

  static getFallbackCreative(fallbackCreativeData) {
    // resolve the template from the adType and render the fallback creative
    // using Mustache templates
    const template = window.adTemplates[fallbackCreativeData.adType];
    return window.Mustache.render(template, fallbackCreativeData);
  }

  triggerFallback() {
    // this function once called is responsible to occupy the AD spot
    // with a fallback creative.
    this.#fallbackTriggered = true;
    this.fallbackAds = JSON.parse(window.fallbackAds);

    // distinguish between mobile, desktop, and full-width ad spots so - in the case where
    // there are fewer ads than ad spots - we don't hide ads we could be showing
    const mobileAdSpots = $('.hybrid-ad-wrapper.show-small.hide-large');
    const desktopAdSpots = $('.hybrid-ad-wrapper.hide-small.show-large');
    const fullWidthAdSpots = $('.hybrid-ad-wrapper').not('.show-small.hide-large').not('.hide-small.show-large');

    this.showFallbackAds(mobileAdSpots);
    this.showFallbackAds(desktopAdSpots);
    this.showFallbackAds(fullWidthAdSpots);

    // add the styling for sidebar ads
    const nativeAdEditorialSelector = '.sidebar .hybrid-ad-wrapper .native-ad-editorial';
    $(nativeAdEditorialSelector).closest('.hybrid-ad-wrapper').addClass('native-ad-editorial-sidebar-border');
  }

  showFallbackAds(adSpots) {
    const { fallbackAds } = this;

    // eslint-disable-next-line consistent-return
    adSpots.each(function(index) {
      // don't show the same ad more than twice - in case the ad spots are more than double the ads
      if (index === fallbackAds.length * 2) { return false; }

      // cycle through ads if there are more ad spots than ads
      const adIndex = index % fallbackAds.length;
      const fallbackCreative = OnBoardDiagnostics.getFallbackCreative(fallbackAds[adIndex]);
      $(this).find('.hybrid-ad-inner-wrapper').html(fallbackCreative);
      // If fallback image contains header_image_logos (ie, the img is unapproved),
      // remove the attribution info button
      if ($(this).find('img[src*="header_image_logos"]').length > 0) {
        $(this).find('.feed__attribution-icon').remove();
      }
    });
  }

  evaluate() {
    // this function examines diagnostic signals and makes a decision
    // to trigger AD fallback
    this.diagnosticPrintOut = this.getDiagnosticPrintOut();
    if (!this.diagnosticPrintOut.isMarketingAllowed) {
      this.triggerFallback();
      return;
    }
    if (!this.diagnosticPrintOut.googleAdUrlAccessible) {
      this.triggerFallback();
      return;
    }
    if (this.#inspectionCycle > 1 && !this.diagnosticPrintOut.isGoogleTagLoaded) {
      this.triggerFallback();
    }
  }

  async inspect() {
    // this function is responsible for calling all the individual inspections
    // finally this function triggers the evaluate. function
    this.#inspectionCycle += 1;
    if (this.#fallbackTriggered) {
      // skip inspection cycle if fallback has already been triggered
      return;
    }
    await this.waitForOsanoBannerInteraction();
    const detectAdBlockPromise = this.detectAdBlock();
    this.inspectLocalStorage();
    this.inspectGoogleTag();
    this.inspectOsanoPreference();
    await detectAdBlockPromise;
    this.evaluate();
  }

  startInspectionCycles() {
    // inspect once immediately
    this.inspect();
    // inspect again after the stipulated inspection delay
    setTimeout(this.inspect.bind(this), this.inspectionDelay);
  }
}

// obd is made available for debugging globally
window.obd = new OnBoardDiagnostics();
window.obd.startInspectionCycles();
